import { AlertTriangle } from 'lucide-react';
import type { ProjectoPLSummary } from '../../types/projecto';
import { formatEuro } from '../../utils/currency';

/** Full amount on a computer; no cents on a phone, where three of them must fit in a row. */
function Amount({ value }: { value: number }) {
  return (
    <>
      <span className="sm:hidden">{formatEuro(Math.round(value)).replace(',00€', '€')}</span>
      <span className="hidden sm:inline">{formatEuro(value)}</span>
    </>
  );
}

function Figure({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div className="min-w-0 rounded-xl bg-gray-50 px-2 sm:px-3 py-2">
      <p className="text-[11px] font-medium text-gray-500 truncate">{label}</p>
      <p className="text-[13px] sm:text-lg font-bold text-brand-ink tabular-nums whitespace-nowrap leading-tight"><Amount value={value} /></p>
      <p className="hidden sm:block text-[11px] text-gray-500 truncate">{hint}</p>
    </div>
  );
}

const Operator = ({ children }: { children: string }) => (
  <span aria-hidden className="text-sm sm:text-xl font-bold text-gray-400">{children}</span>
);

/** The whole P&L in one sentence: the client pays − costs = margin. */
export function ProjectoSummaryBar({ summary, targetMarginPct = 0 }: { summary: ProjectoPLSummary; targetMarginPct?: number }) {
  const { fixedBudget, revenue, costs, margin, marginPct, missingCostLines } = summary;
  const noBudget = fixedBudget && revenue <= 0;
  const belowTarget = fixedBudget && targetMarginPct > 0 && marginPct < targetMarginPct;
  const marginColor = margin < 0 ? 'text-red-300' : belowTarget ? 'text-amber-300' : 'text-brand-lime';

  return (
    <div className="flex-shrink-0 border-b border-gray-200 bg-white px-3 sm:px-0 py-2.5">
      <div className="max-w-5xl mx-auto sm:px-6 space-y-2">
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1 sm:gap-3" data-testid="projecto-summary">
          <Figure
            label={fixedBudget ? 'Orçamento' : 'O cliente paga'}
            value={revenue}
            hint={fixedBudget ? 'Valor fechado, sem IVA' : 'Soma das linhas, sem IVA'}
          />
          <Operator>−</Operator>
          <Figure label="Custos" value={costs} hint="O que a agência paga" />
          <Operator>=</Operator>
          <div className="min-w-0 rounded-xl bg-brand-ink px-2 sm:px-3 py-2">
            <p className="text-[11px] font-medium text-white/70 truncate">Margem</p>
            <p className={`text-[13px] sm:text-lg font-bold tabular-nums whitespace-nowrap leading-tight ${marginColor}`}><Amount value={margin} /></p>
            <p className="hidden sm:block text-[11px] text-white/70 truncate">
              {revenue > 0 ? `${marginPct.toLocaleString('pt-PT', { maximumFractionDigits: 1 })}% do que o cliente paga` : 'Ainda sem receita'}
              {belowTarget && ` · objetivo ${targetMarginPct}%`}
            </p>
          </div>
        </div>
        {noBudget && (
          <p className="flex items-center gap-1.5 text-xs text-amber-800">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            Falta indicar o orçamento do cliente para calcular a margem.
          </p>
        )}
        {missingCostLines > 0 && (
          <p className="flex items-start gap-1.5 text-xs text-amber-800">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
            {missingCostLines === 1 ? '1 linha sem custo' : `${missingCostLines} linhas sem custo`}: a margem está acima da real até preencheres o que pagas.
          </p>
        )}
      </div>
    </div>
  );
}
